import { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { ReactNode } from 'react'

import type { IntegrationAccountFormValues } from '@/lib/schemas/grower-camera-schemas'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { createIntegrationAccountSchema } from '@/lib/schemas/grower-camera-schemas'
import { m } from '@/lib/paraglide/messages'

export interface IntegrationAccount {
    enabled: boolean
    base_url: string
    username: string
    has_password: boolean
}

interface Props {
    /** Tiêu đề thẻ — phải nói rõ tích hợp nào (máy vò chè / camera hộ trồng). */
    title: ReactNode
    description: ReactNode
    enabledHint: ReactNode
    baseUrlPlaceholder: string
    baseUrlHint?: ReactNode
    saveLabel: string
    /** Bắt buộc HTTPS (camera) hay cho cả http:// (máy vò trong mạng nội bộ). */
    requireHttps: boolean
    settings: IntegrationAccount | null | undefined
    canWrite: boolean
    isSaving: boolean
    onSave: (values: IntegrationAccountFormValues) => Promise<unknown>
    /** Cho test và cho các thẻ khác nhau không trùng id input. */
    idPrefix: string
}

/**
 * Thẻ nhập tài khoản Basic Auth của MỘT tích hợp SmartLab.
 *
 * Dùng chung cho cả máy vò chè và camera hộ trồng: hai tích hợp có đúng một bộ
 * trường (bật/tắt, base URL, tên đăng nhập, mật khẩu) nên tách thành component
 * này thay vì nhân đôi form. Mỗi thẻ có form và nút Lưu RIÊNG — hai bản ghi,
 * hai request; nút lưu chung sẽ khiến người sửa camera ghi đè luôn cấu hình máy
 * vò đang chạy sản xuất.
 */
export function IntegrationAccountCard({
    title,
    description,
    enabledHint,
    baseUrlPlaceholder,
    baseUrlHint,
    saveLabel,
    requireHttps,
    settings,
    canWrite,
    isSaving,
    onSave,
    idPrefix,
}: Props) {
    const hasPassword = settings?.has_password ?? false

    const form = useForm<IntegrationAccountFormValues>({
        resolver: zodResolver(
            createIntegrationAccountSchema({ hasPassword, requireHttps }),
        ),
        defaultValues: {
            enabled: false,
            base_url: '',
            username: '',
            password: '',
        },
    })

    useEffect(() => {
        if (!settings) return
        form.reset({
            enabled: settings.enabled,
            base_url: settings.base_url,
            username: settings.username,
            password: '',
        })
        // form là ref ổn định của RHF; reset chỉ chạy khi dữ liệu server đổi.
    }, [settings, form])

    const handleSubmit = form.handleSubmit(async (values) => {
        try {
            await onSave(values)
        } catch {
            // Hook lưu đã toast message của backend. Bắt lại ở đây để promise
            // của handleSubmit không reject ra ngoài, và GIỮ NGUYÊN những gì
            // người dùng vừa nhập để họ sửa rồi lưu lại.
            return
        }
        // Xoá ô mật khẩu sau khi lưu: giá trị thật không bao giờ được đọc lại.
        form.setValue('password', '')
    })

    const baseUrlId = `${idPrefix}-base-url`
    const usernameId = `${idPrefix}-username`
    const passwordId = `${idPrefix}-password`

    return (
        <FormProvider {...form}>
            {/* Form lồng trong trang có nhiều thẻ: chặn submit nổi lên thẻ khác. */}
            <form
                noValidate
                onSubmit={(event) => {
                    event.stopPropagation()
                    void handleSubmit(event)
                }}
            >
                <Card>
                    <CardHeader>
                        <CardTitle>{title}</CardTitle>
                        <p className="text-sm text-muted-foreground">
                            {description}
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* relative: Switch trong form sinh <input> ẩn
                            position absolute cạnh nút. Thiếu cha relative nó
                            định vị theo cả trang, lọt khỏi vùng cuộn của
                            <main> và đẻ thêm thanh cuộn thứ hai. */}
                        <div className="relative flex items-center justify-between gap-4">
                            <div className="space-y-0.5">
                                <div className="text-sm font-medium">
                                    {m.smartlab_settings_enabled()}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {enabledHint}
                                </div>
                            </div>
                            <Controller
                                control={form.control}
                                name="enabled"
                                render={({ field }) => (
                                    <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                        disabled={!canWrite}
                                        aria-label={m.smartlab_settings_enabled()}
                                    />
                                )}
                            />
                        </div>

                        {/* Label gắn tay bằng htmlFor thay vì dùng <Field>:
                            trang này có HAI thẻ, id phải khác nhau giữa hai
                            form, còn <Field> lấy chính tên field làm id nên
                            hai ô sẽ trùng id và label trỏ sai ô. */}
                        <div className="space-y-1.5">
                            <Label htmlFor={baseUrlId}>
                                {m.smartlab_settings_base_url()}
                            </Label>
                            <Input
                                id={baseUrlId}
                                placeholder={baseUrlPlaceholder}
                                disabled={!canWrite}
                                {...form.register('base_url')}
                            />
                            <FieldError name="base_url" />
                            {baseUrlHint && (
                                <p className="text-xs text-muted-foreground">
                                    {baseUrlHint}
                                </p>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor={usernameId}>
                                {m.smartlab_settings_username()}
                            </Label>
                            <Input
                                id={usernameId}
                                autoComplete="off"
                                disabled={!canWrite}
                                {...form.register('username')}
                            />
                            <FieldError name="username" />
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor={passwordId}>
                                {m.smartlab_settings_password()}
                            </Label>
                            <Input
                                id={passwordId}
                                type="password"
                                autoComplete="new-password"
                                placeholder={
                                    hasPassword
                                        ? m.smartlab_settings_password_set_placeholder()
                                        : m.grower_camera_settings_password_placeholder()
                                }
                                disabled={!canWrite}
                                {...form.register('password')}
                            />
                            <FieldError name="password" />
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {hasPassword
                                ? m.smartlab_settings_password_keep_hint()
                                : m.grower_camera_settings_password_empty_hint()}
                        </p>

                        <div className="flex justify-end">
                            {/* Không disable theo validate: bấm được thì mới
                                thấy lỗi cụ thể dưới ô sai. Chỉ disable khi
                                đang gửi request (chống double submit). */}
                            <Button
                                type="submit"
                                disabled={!canWrite || isSaving}
                            >
                                {isSaving ? m.common_saving() : saveLabel}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </form>
        </FormProvider>
    )
}
